import { useEffect, useReducer, useState, type PropsWithChildren } from 'react';

import {
  BackgroundJobsContext,
  createJobScheduler,
  isJobActive,
  jobsReducer,
  NO_JOBS,
  type StartJobInput,
} from './background-jobs';

/**
 * Long-running work that outlives the component which started it.
 *
 * The problem this solves is ownership, not concurrency. An upload or a
 * conversion used to be awaited *by* the page that started it, so navigating
 * away left the promise running with nowhere to report: no progress bar, no
 * completion, no refetch. The request itself was never cancelled — what was lost
 * was every trace of it in the UI.
 *
 * Jobs run here instead. The provider sits above the router outlet, so a job and
 * its progress survive every navigation, and `BackgroundJobsPanel` renders them
 * from wherever the user has got to.
 *
 * Jobs are in-memory and do not survive a reload; the provider warns before one
 * while work is in flight. Persisting a finished result across reloads is a
 * separate concern — see GEN-2320.
 *
 * The job model, reducer and queue live in `./background-jobs`. This file holds
 * only the component, so nothing here has to be memoization-hostile.
 */

/**
 * Warn before a reload or tab close while work is in flight.
 *
 * A real external-system sync (the window), which is what `useEffect` is for.
 * Jobs are in-memory, so a reload genuinely does lose them — this is what stands
 * between a stray ⌘R and a conversion the user waited on.
 */
function useUnloadWarning(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    function warn(event: BeforeUnloadEvent): void {
      // Browsers show their own copy; preventing the event is the whole API.
      event.preventDefault();
    }
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [enabled]);
}

export function BackgroundJobsProvider({ children }: PropsWithChildren) {
  const [jobs, dispatch] = useReducer(jobsReducer, NO_JOBS);
  // One scheduler for the life of the provider. `useState`'s initializer runs
  // once, and `dispatch` is stable, so this never needs replacing.
  const [scheduler] = useState(() => createJobScheduler(dispatch));

  // Queued jobs count: the user has committed to that work, so a reload would
  // lose it just the same.
  useUnloadWarning(jobs.some(isJobActive));

  function retryJob(id: string): void {
    const job = jobs.find((candidate) => candidate.id === id);
    // The scheduler decides whether this may re-run; it owns the invariants.
    if (job) scheduler.retry(job);
  }

  return (
    <BackgroundJobsContext.Provider
      value={{
        jobs,
        startJob: (input: StartJobInput) => scheduler.start(input),
        retryJob,
        dismissJob: (id: string) => scheduler.dismiss(id),
        dismissFinishedJobs: () => dispatch({ type: 'finishedDismissed' }),
      }}
    >
      {children}
    </BackgroundJobsContext.Provider>
  );
}
